import React, { useCallback, useEffect } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Loader2, LogIn, ShieldX } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import AdminShell from './admin/AdminShell';
import CustomerSheet from './admin/CustomerSheet';
import { SECTION_ALIASES, SECTION_IDS, sectionById } from './admin/sections';
import { Btn } from './admin/ui';
import OverviewPanel from './admin/OverviewPanel';
import RevenuePanel from './admin/RevenuePanel';
import KpiPanel from './admin/KpiPanel';
import GesturesPanel from './admin/GesturesPanel';
import CustomersPanel from './admin/CustomersPanel';
import SegmentsPanel from './admin/SegmentsPanel';
import SupportPanel from './admin/SupportPanel';
import RedditPanel from './admin/RedditPanel';
import UnaskedWaitlistPanel from './admin/UnaskedWaitlistPanel';
import SettingsPanel from './admin/SettingsPanel';
import LogsPanel from './admin/LogsPanel';

/* While the console is open, <body> carries the admin theme instead of the
   app's light/dark theme. Dialogs, sheets, menus and toasts render in portals
   on <body>, outside the console's own tree, so this is what keeps them in the
   same light theme as the page. */
function useAdminBodyScope() {
  useEffect(() => {
    const body = document.body;
    const appTheme = ['dark', 'dark-forest', 'light-forest'].filter((c) => body.classList.contains(c));
    appTheme.forEach((c) => body.classList.remove(c));
    body.classList.add('admin-scope');
    return () => {
      body.classList.remove('admin-scope');
      appTheme.forEach((c) => body.classList.add(c));
    };
  }, []);
}

function Gate({ icon: Icon, title, description, action, loading }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-background px-4">
      {loading ? (
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      ) : (
        <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 text-center">
          <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-secondary">
            <Icon className="h-5 w-5 text-muted-foreground" />
          </span>
          <h1 className="mt-4 text-base font-semibold text-foreground">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          {action && <div className="mt-5 flex justify-center">{action}</div>}
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  useAdminBodyScope();
  const { user, isAdmin, loading } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const { section: rawSection } = useParams();
  const [params, setParams] = useSearchParams();

  const section = rawSection && SECTION_IDS.has(rawSection) ? rawSection : 'overview';

  useEffect(() => {
    const label = sectionById(section)?.label;
    document.title = `${label ? `${label} · ` : ''}Admin · Better Husband`;
    return () => { document.title = 'Better Husband'; };
  }, [section]);

  const goTo = useCallback((id) => navigate(`/admin/${id}`), [navigate]);

  const setParam = useCallback((key, value) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      return next;
    });
  }, [setParams]);
  // Optionally straight onto one of the customer's tabs (?tab=gestures).
  const openCustomer = useCallback((id, tab) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('user', id);
      if (tab) next.set('tab', tab);
      else next.delete('tab');
      return next;
    });
  }, [setParams]);
  const closeCustomer = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('user');
      next.delete('tab');
      return next;
    });
  }, [setParams]);

  // isAdmin stays false until the profile has loaded, so wait for it rather
  // than flashing "No admin access".
  if (loading && !isAdmin) return <Gate loading />;
  if (!user) {
    return (
      <Gate
        icon={LogIn}
        title="Sign in to open the admin console"
        description="Sign in with an admin account, then come back to this page."
        action={<Btn onClick={() => navigate('/login')}>Sign in</Btn>}
      />
    );
  }
  if (!isAdmin) {
    return (
      <Gate
        icon={ShieldX}
        title="No admin access"
        description={`${user.email || 'This account'} is not an admin account.`}
        action={<Btn variant="outline" onClick={() => navigate('/dashboard')}>Back to the app</Btn>}
      />
    );
  }

  // Old tab names and the retired /admin/tickets page.
  if (rawSection && SECTION_ALIASES[rawSection]) {
    return <Navigate to={`/admin/${SECTION_ALIASES[rawSection]}${location.search}`} replace />;
  }
  if (rawSection && !SECTION_IDS.has(rawSection)) return <Navigate to="/admin" replace />;

  const panels = {
    overview: () => <OverviewPanel onNavigate={goTo} />,
    revenue: () => <RevenuePanel onOpenCustomer={openCustomer} />,
    kpis: () => <KpiPanel onOpenCustomer={openCustomer} />,
    gestures: () => <GesturesPanel onOpenCustomer={openCustomer} />,
    customers: () => <CustomersPanel onOpenCustomer={openCustomer} />,
    segments: () => <SegmentsPanel />,
    support: () => (
      <SupportPanel
        selectedId={params.get('ticket')}
        onSelect={(id) => setParam('ticket', id)}
        onOpenCustomer={openCustomer}
      />
    ),
    reddit: () => <RedditPanel />,
    unasked: () => <UnaskedWaitlistPanel />,
    settings: () => <SettingsPanel />,
    logs: () => <LogsPanel />,
  };

  return (
    <AdminShell section={section} onNavigate={goTo} onOpenCustomer={openCustomer}>
      {panels[section]()}
      <CustomerSheet userId={params.get('user')} initialTab={params.get('tab')} onClose={closeCustomer} />
    </AdminShell>
  );
}
